import { doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface DeviceInfo {
  ipAddress: string;
  userAgent: string;
  operatingSystem: string;
  browser: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  lastSeen: any; // Firestore Timestamp
  country?: string;
  city?: string;
}

export class DeviceInfoService {
  /**
   * Detect the user's operating system from user agent
   */
  static detectOS(userAgent: string): string {
    if (userAgent.includes('Windows NT 10.0')) return 'Windows 10/11';
    if (userAgent.includes('Windows NT 6.3')) return 'Windows 8.1';
    if (userAgent.includes('Windows NT 6.2')) return 'Windows 8';
    if (userAgent.includes('Windows NT 6.1')) return 'Windows 7';
    if (userAgent.includes('Windows NT 6.0')) return 'Windows Vista';
    if (userAgent.includes('Windows NT 5.1')) return 'Windows XP';
    if (userAgent.includes('Windows NT 5.0')) return 'Windows 2000';
    if (userAgent.includes('Mac OS X')) return 'macOS';
    if (userAgent.includes('Mac OS')) return 'macOS (Legacy)';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iPhone')) return 'iOS';
    if (userAgent.includes('iPad')) return 'iPadOS';
    if (userAgent.includes('BlackBerry')) return 'BlackBerry OS';
    if (userAgent.includes('Windows Phone')) return 'Windows Phone';
    return 'Unknown OS';
  }

  /**
   * Detect the user's browser from user agent
   */
  static detectBrowser(userAgent: string): string {
    if (userAgent.includes('Edg/')) return 'Microsoft Edge';
    if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return 'Google Chrome';
    if (userAgent.includes('Firefox/')) return 'Mozilla Firefox';
    if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari';
    if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) return 'Opera';
    if (userAgent.includes('Trident/')) return 'Internet Explorer';
    return 'Unknown Browser';
  }

  /**
   * Detect device type from user agent
   */
  static detectDeviceType(userAgent: string): 'desktop' | 'mobile' | 'tablet' {
    if (userAgent.includes('Mobile') || (userAgent.includes('Android') && userAgent.includes('Mobile'))) {
      return 'mobile';
    }
    if (userAgent.includes('iPad') || userAgent.includes('Tablet')) {
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * Get user's IP address using a public API
   */
  static async getIPAddress(): Promise<string> {
    try {
      console.log('🔍 Starting IP address detection...');
      
      // Try multiple IP detection services for reliability
      const services = [
        'https://api.ipify.org?format=json',
        'https://ipapi.co/json/',
        'https://httpbin.org/ip'
      ];

      for (const service of services) {
        try {
          console.log(`🌐 Trying IP service: ${service}`);
          const response = await fetch(service, { 
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            }
          });
          
          console.log(`📡 Response status: ${response.status}`);
          
          if (response.ok) {
            const data = await response.json();
            console.log('📊 IP service response:', data);
            const ip = data.ip || data.query || data.origin || 'Unknown';
            console.log(`✅ IP detected: ${ip}`);
            return ip;
          } else {
            console.warn(`❌ Service ${service} returned status: ${response.status}`);
          }
        } catch (error) {
          console.warn(`❌ IP detection service failed: ${service}`, error);
          continue;
        }
      }
      
      console.warn('⚠️ All IP detection services failed, using fallback');
      return 'Unknown';
    } catch (error) {
      console.error('💥 Error getting IP address:', error);
      return 'Unknown';
    }
  }

  /**
   * Get location information based on IP address
   */
  static async getLocationInfo(ipAddress: string): Promise<{ country?: string; city?: string }> {
    if (ipAddress === 'Unknown') {
      console.log('⚠️ Cannot get location info for unknown IP');
      return {};
    }

    try {
      console.log(`🌍 Getting location info for IP: ${ipAddress}`);
      const response = await fetch(`https://ipapi.co/${ipAddress}/json/`);
      console.log(`📍 Location service response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🗺️ Location data:', data);
        const result = {
          country: data.country_name || undefined,
          city: data.city || undefined
        };
        console.log('✅ Location info extracted:', result);
        return result;
      } else {
        console.warn(`❌ Location service failed with status: ${response.status}`);
      }
    } catch (error) {
      console.warn('❌ Error getting location info:', error);
    }

    return {};
  }

  /**
   * Collect comprehensive device information
   */
  static async collectDeviceInfo(): Promise<DeviceInfo> {
    console.log('🚀 Starting device info collection...');
    
    const userAgent = navigator.userAgent;
    console.log('📱 User Agent:', userAgent);
    
    const ipAddress = await this.getIPAddress();
    const locationInfo = await this.getLocationInfo(ipAddress);

    const deviceInfo = {
      ipAddress,
      userAgent,
      operatingSystem: this.detectOS(userAgent),
      browser: this.detectBrowser(userAgent),
      deviceType: this.detectDeviceType(userAgent),
      lastSeen: serverTimestamp(),
      ...locationInfo
    };

    console.log('📊 Collected device info:', deviceInfo);
    return deviceInfo;
  }

  /**
   * Update user's device information in Firestore
   * Creates the document if it doesn't exist, updates if it does
   */
  static async updateUserDeviceInfo(userId: string, deviceInfo: DeviceInfo): Promise<void> {
    try {
      console.log(`💾 Updating device info for user: ${userId}`);
      const userRef = doc(db, 'users', userId);
      
      const updateData = {
        deviceInfo: {
          ...deviceInfo,
          lastSeen: serverTimestamp()
        },
        lastActive: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('📝 Firestore update data:', updateData);
      
      // Use setDoc with merge: true to create document if it doesn't exist
      await setDoc(userRef, updateData, { merge: true });
      console.log('✅ Device info updated successfully in Firestore');
    } catch (error) {
      console.error('❌ Error updating user device info:', error);
      throw error;
    }
  }

  /**
   * Get device info from user data
   */
  static getDeviceInfoFromUser(user: any): DeviceInfo | null {
    return user.deviceInfo || null;
  }

  /**
   * Format device info for display
   */
  static formatDeviceInfo(deviceInfo: DeviceInfo): string {
    return `${deviceInfo.operatingSystem} • ${deviceInfo.browser} • ${deviceInfo.deviceType}`;
  }

  /**
   * Get device icon based on OS
   */
  static getDeviceIcon(os: string): string {
    if (os.includes('Windows')) return '🪟';
    if (os.includes('macOS')) return '🍎';
    if (os.includes('Linux')) return '🐧';
    if (os.includes('Android')) return '🤖';
    if (os.includes('iOS') || os.includes('iPadOS')) return '📱';
    return '💻';
  }

  /**
   * Get browser icon
   */
  static getBrowserIcon(browser: string): string {
    if (browser.includes('Chrome')) return '🌐';
    if (browser.includes('Firefox')) return '🦊';
    if (browser.includes('Safari')) return '🧭';
    if (browser.includes('Edge')) return '🔷';
    if (browser.includes('Opera')) return '🎭';
    return '🌐';
  }
}
